import type { ConnectivityPort } from '@Domain/Ports/Connectivity/ConnectivityPort';
import type { LocalBufferPort } from '@Domain/Ports/LocalBuffer/LocalBufferPort';
import type {
	OutboxHydrationState,
	OutboxItem,
	OutboxItemState,
	OutboxPort,
	TransferHandler,
	TransferOutcome,
	TransferPhase,
	TransferSegment,
	VerifiedSegmentDigest,
} from '@Domain/Ports/Outbox/OutboxPort';
import WebConnectivityAdapter from '@Infra/Connectivity/WebConnectivityAdapter';
import IndexedDbLocalBufferAdapter from '@Infra/LocalBuffer/IndexedDbLocalBufferAdapter';
import computeBackoffDelayMs from '@Infra/Outbox/backoff';
import IndexedDbOutboxStore, { type OutboxRecord } from '@Infra/Outbox/IndexedDbOutboxStore';
import computeSegmentDigestHex from '@Infra/Outbox/segmentDigest';

/** Injected time source: lets every backoff, staleness and purge window be asserted exactly in tests instead of racing the wall clock. */
type Clock = {
	now(): number;
};

/**
 * Whatever `setTimeout` actually returns in the running program — this lets the handle type track the
 * real global instead of assuming the browser's `number`, since a `@types/node` declaration reachable
 * from this program's other ambient types can shift that return type.
 */
type TimerHandle = ReturnType<typeof globalThis.setTimeout>;

/** Injected scheduling primitive, expressed as a handle-based pair rather than the raw global functions, so a fake timer needs no relationship to `vi.useFakeTimers()`. */
type Timer = {
	schedule(callback: () => void, delayMs: number): TimerHandle;
	cancel(handle: TimerHandle): void;
};

/** Injected identifier source for new queue entries. */
type IdGenerator = {
	generate(): string;
};

/** The four ways the integrity barrier can refuse a deletion, named after the acceptance criteria that require them distinguishable. */
type IntegrityFailureReason = 'divergent' | 'missing' | 'extra' | 'mismatched-segment';

const OUTBOX_TRANSFER_LOCK_NAME = 'dixit-outbox-transfer';

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * A backfilled retention never lets an already-passed (or near-passed) deadline destroy an item at
 * the very hydration pass that discovers it: the deadline is floored to `now + 72h` instead, so the
 * warning always has a chance to show before deletion.
 */
const RETENTION_BACKFILL_GRACE_PERIOD_MS = 72 * 60 * 60 * 1000;

/**
 * How long an `'expired'` record's display trace survives, regardless of its owner. This bounds a
 * display trace, not a data-retention rule, hence it is a constant of this engine rather than a
 * server-served policy: it must outlive a colleague away for several weeks, which is the very case
 * that motivates it.
 */
const EXPIRED_TRACE_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

const NO_HANDLER_MESSAGE = "Aucun gestionnaire de transfert n'est enregistré pour ce type d'élément.";

const TRANSIENT_READ_FAILURE_MESSAGE = 'La lecture des fragments enregistrés en local a échoué.';

const UNEXPECTED_HANDLER_FAILURE_MESSAGE =
	'Le transfert a échoué de manière inattendue ; une nouvelle tentative est programmée.';

const ITEM_NOT_FOUND_MESSAGE = 'Cet élément est introuvable dans la file de synchronisation.';

const OWNER_MISMATCH_MESSAGE =
	"Impossible de créer cet élément : son propriétaire ne correspond pas à l'utilisateur pour lequel le moteur de synchronisation a démarré.";

const NOT_A_DRAFT_MESSAGE =
	"Seul un élément à l'état brouillon peut être marqué comme prêt à être transféré.";

/** States whose bytes are still on the device and that the retention deadline erases. */
const isExpirableState = (state: OutboxItemState): boolean =>
	state === 'draft' || state === 'pending' || state === 'failed' || state === 'orphaned';

const NOT_RESTARTABLE_MESSAGE = 'Seul un enregistrement supprimé du dossier peut être recréé.';
const NOT_RETRYABLE_MESSAGE = 'Seul un élément en attente ou en échec peut être relancé.';

const INTEGRITY_FAILURE_MESSAGES: Readonly<Record<IntegrityFailureReason, string>> = {
	divergent:
		"Vérification d'intégrité échouée : le condensat renvoyé par le serveur ne correspond pas au contenu transféré pour au moins un segment.",
	missing:
		"Vérification d'intégrité échouée : le serveur n'a pas renvoyé de condensat pour au moins un segment transféré.",
	extra: "Vérification d'intégrité échouée : le serveur a renvoyé plus de condensats qu'il n'y a de segments transférés.",
	'mismatched-segment':
		"Vérification d'intégrité échouée : le serveur a renvoyé un condensat pour un segment qui n'a pas été transféré.",
};

/** Stable, frozen and shared: every "before start / after stop" reading returns this exact reference. */
const FROZEN_EMPTY_SNAPSHOT: readonly OutboxItem[] = Object.freeze([]);

/**
 * Owns the on-device synchronisation queue: persistence (delegated to `IndexedDbOutboxStore`),
 * ordering, one-transfer-at-a-time concurrency, connectivity gating, backoff, state transitions,
 * and the integrity barrier that alone authorises deleting bytes.
 *
 * Construction does nothing observable — no database, no lock, no subscription — everything starts
 * in `start()`. Every browser capability is received through the constructor, defaulted from the
 * real global only at construction time; `lockManager` is the one legitimately absent from some
 * browsers, hence its explicitly optional type.
 *
 * Integrity barrier: this class assembles each segment and computes its own SHA-256 digest first,
 * keeping it to itself. The handler only ever receives `{ segment, blob }` — never the digest it is
 * expected to return — so it cannot echo the target value back by mistake. Once the handler resolves
 * with `'confirmed'`, this class enters `'verifying'` itself and compares the server's digests against
 * its own, segment by segment and by count: a divergent value, a missing segment, more digests than
 * segments sent, or a digest for a segment never sent, all fail closed with the bytes untouched.
 * `integrityFailureCount` decides where that failure lands: the first one (`0 → 1`) is transient —
 * the item goes back to `'pending'` with backoff, so a handler that skips segments the server
 * already holds gets an automatic further attempt that resends everything in full, letting the
 * server's own upsert overwrite a diverging position — a second failure is a local corruption a
 * further resend will not heal, and becomes `'failed'` for good. A handler that lies about what the
 * server said cannot be caught by this comparison — the handler is the one holding the bytes — which
 * is why the transfer endpoint's own honesty is a constraint on whoever writes that handler, not
 * something this class can enforce.
 *
 * `'retry'` reschedules through backoff and keeps trying automatically; `'failed'` — whether reported
 * by the handler or produced by a second failed integrity check — stops automatic retry for good, and
 * only an explicit `retryNow()` resumes it. Either way the bytes stay on the device until an explicit
 * `discard()`, the only door out of a non-expired queue entry that is not a successful, verified
 * transfer.
 *
 * Retention and expiry: `retentionMs` is carried by this engine (`start()`, refreshable through
 * `setRetention()`), never supplied by `createItem`'s caller. Elapsed time is always two readings of
 * the same injected clock, `now() - createdAt`, never a comparison against a server time — a clock
 * that merely runs behind never expires anything early, which is the only direction a mistake here
 * is allowed to fail toward: erasing a still-wanted recording is worse than a late erasure. Expiry is
 * evaluated at hydration across every owner on this device — deliberately unscoped, since it closes
 * the residue of an item left behind by another user who is not present to trigger it — and,
 * scoped to the current owner only, on every queue-processing cycle and connectivity change. An
 * `'expired'` transition is always written before its bytes are deleted, and a record already
 * `'expired'` whose bytes still exist gets `deleteItem` called again at the next hydration —
 * `deleteItem` is idempotent, which is what makes that recall safe. `context` and `durationMs`
 * survive expiry, unlike confirmation, which erases them: what is lost is still worth naming. A
 * retention policy change never shortens a deadline already granted; `setRetention` only backfills
 * items still at `retentionMs === null`.
 *
 * `snapshot()` reads a private in-memory cache and never touches storage: the cache is rebuilt on
 * hydration, on every mutation, and on every state transition, and `snapshot()` returns the exact same
 * array reference until the next rebuild. Before `start()` (and after `stop()`) it returns the same
 * frozen empty array every time, never the unfiltered queue, because owner filtering only exists once
 * the runner has hydrated for a given `ownerUserId`.
 *
 * `hydrationState()` tells a reader whether that cache has ever been read from disk at all, which
 * `snapshot()` alone cannot: an empty array before hydration and an empty array after a hydration
 * failure are indistinguishable through `snapshot()`, since the failure path resolves through
 * `stop()` and inherits the very same frozen empty reference the pre-start reading already served.
 * `hydrationState()` is therefore the only signal a caller can rely on to tell "not read yet" apart
 * from "read, and empty" apart from "could not be read".
 *
 * Owner scoping is checked on both sides of that cache, not only on reads: `createItem` rejects an
 * `ownerUserId` different from the one this runner was started with, and `markReady`, `retryNow` and
 * `discard` each reject a record whose owner differs from it, reporting exactly the same error as an
 * unknown id so a caller is never told a foreign item exists. Without this, the cache filter alone
 * would still keep such a write out of every snapshot, leaving an orphaned record on disk that
 * nothing ever selects for transfer or purges.
 *
 * A single transfer runs at a time across tabs through a named Web Lock; where `navigator.locks` is
 * unavailable, this degrades to single-tab enforcement only — two tabs without the API can each start
 * a transfer of a different item at the same time. The safety net that remains in that case is the
 * transfer endpoint's own idempotency, which it needs anyway to support this class's automatic
 * retries: a caller relying on this class in a browser without Web Locks must not assume exclusivity
 * across tabs.
 */
class OutboxRunner implements OutboxPort {
	private isStarted: boolean;
	private ownerUserId: number | null;
	/** Retention this engine currently carries, in milliseconds; `null` means "never expires". */
	private retentionMs: number | null;
	private hydrationStatus: OutboxHydrationState;
	private readonly handlersByKind: Map<string, TransferHandler>;
	private readonly itemsCache: Map<string, OutboxItem>;
	private cachedSnapshotArray: readonly OutboxItem[];
	private readonly listeners: Set<(items: readonly OutboxItem[]) => void>;
	private isProcessingQueue: boolean;
	private scheduledTimerHandle: TimerHandle | null;
	private currentTransferAbortController: AbortController | null;
	private currentTransferItemId: string | null;
	private unsubscribeConnectivity: (() => void) | null;

	constructor(
		private readonly connectivityPort: ConnectivityPort = new WebConnectivityAdapter(),
		private readonly localBufferPort: LocalBufferPort = new IndexedDbLocalBufferAdapter(),
		private readonly outboxStore: IndexedDbOutboxStore = new IndexedDbOutboxStore(),
		private readonly computeBackoffDelay: (attemptCount: number) => number = computeBackoffDelayMs,
		private readonly computeDigest: (segment: Blob) => Promise<string> = computeSegmentDigestHex,
		private readonly lockManager: LockManager | undefined = globalThis.navigator?.locks,
		private readonly clock: Clock = { now: () => Date.now() },
		private readonly timer: Timer = {
			schedule: (callback, delayMs) => globalThis.setTimeout(callback, delayMs),
			cancel: (handle) => globalThis.clearTimeout(handle),
		},
		private readonly idGenerator: IdGenerator = { generate: () => globalThis.crypto.randomUUID() },
	) {
		this.isStarted = false;
		this.ownerUserId = null;
		this.retentionMs = null;
		this.hydrationStatus = 'pending';
		this.handlersByKind = new Map();
		this.itemsCache = new Map();
		this.cachedSnapshotArray = FROZEN_EMPTY_SNAPSHOT;
		this.listeners = new Set();
		this.isProcessingQueue = false;
		this.scheduledTimerHandle = null;
		this.currentTransferAbortController = null;
		this.currentTransferItemId = null;
		this.unsubscribeConnectivity = null;
	}

	registerHandler(kind: string, handler: TransferHandler): () => void {
		this.handlersByKind.set(kind, handler);
		this.scheduleQueueProcessing();

		return () => {
			if (this.handlersByKind.get(kind) === handler) {
				this.handlersByKind.delete(kind);
			}
		};
	}

	async createItem(input: {
		readonly kind: string;
		readonly mimeType: string;
		readonly context: Readonly<Record<string, string | number>>;
		readonly ownerUserId: number;
	}): Promise<OutboxItem> {
		this.assertStarted("La création d'un élément de la file");
		if (input.ownerUserId !== this.ownerUserId) {
			throw new Error(OWNER_MISMATCH_MESSAGE);
		}

		const now = this.clock.now();
		const record: OutboxRecord = {
			id: this.idGenerator.generate(),
			kind: input.kind,
			state: 'draft',
			mimeType: input.mimeType,
			createdAt: now,
			updatedAt: now,
			durationMs: null,
			pendingSince: null,
			confirmedAt: null,
			attempts: 0,
			nextAttemptAt: null,
			progress: null,
			lastErrorMessage: null,
			ownerUserId: input.ownerUserId,
			context: input.context,
			retentionMs: this.retentionMs,
			expiredAt: null,
			integrityFailureCount: 0,
		};

		await this.outboxStore.save(record);
		const item = await this.composeItem(record);
		this.upsertCacheEntry(item);

		return item;
	}

	async markReady(itemId: string, input: { readonly durationMs: number | null }): Promise<void> {
		this.assertStarted("Le passage en prêt d'un élément");

		const record = await this.findOwnedRecordOrThrow(itemId);
		if (record.state !== 'draft') {
			throw new Error(NOT_A_DRAFT_MESSAGE);
		}

		const now = this.clock.now();
		const updatedRecord: OutboxRecord = {
			...record,
			state: 'pending',
			durationMs: input.durationMs,
			pendingSince: now,
			nextAttemptAt: null,
			updatedAt: now,
		};

		await this.outboxStore.save(updatedRecord);
		const item = await this.composeItem(updatedRecord);
		this.upsertCacheEntry(item);
		this.scheduleQueueProcessing();
	}

	snapshot(): readonly OutboxItem[] {
		if (!this.isStarted) {
			return FROZEN_EMPTY_SNAPSHOT;
		}

		return this.cachedSnapshotArray;
	}

	subscribe(listener: (items: readonly OutboxItem[]) => void): () => void {
		this.listeners.add(listener);

		return () => {
			this.listeners.delete(listener);
		};
	}

	hydrationState(): OutboxHydrationState {
		return this.hydrationStatus;
	}

	async retryNow(itemId: string): Promise<void> {
		this.assertStarted("La relance d'un élément");

		const record = await this.findOwnedRecordOrThrow(itemId);
		if (record.state !== 'pending' && record.state !== 'failed') {
			throw new Error(NOT_RETRYABLE_MESSAGE);
		}

		const now = this.clock.now();
		const updatedRecord: OutboxRecord = {
			...record,
			state: 'pending',
			nextAttemptAt: null,
			pendingSince: record.pendingSince ?? now,
			updatedAt: now,
		};

		await this.outboxStore.save(updatedRecord);
		const item = await this.composeItem(updatedRecord);
		this.upsertCacheEntry(item);
		this.scheduleQueueProcessing();
	}

	async restart(itemId: string): Promise<void> {
		this.assertStarted("La recréation d'un élément");

		const record = await this.findOwnedRecordOrThrow(itemId);
		if (record.state !== 'orphaned') {
			throw new Error(NOT_RESTARTABLE_MESSAGE);
		}

		const now = this.clock.now();
		const restartedRecord: OutboxRecord = {
			...record,
			state: 'pending',
			attempts: 0,
			nextAttemptAt: null,
			progress: null,
			lastErrorMessage: null,
			pendingSince: now,
			updatedAt: now,
		};

		await this.outboxStore.save(restartedRecord);
		const item = await this.composeItem(restartedRecord);
		this.upsertCacheEntry(item);
		this.scheduleQueueProcessing();
	}

	async discard(itemId: string): Promise<void> {
		this.assertStarted('La mise au rebut est');

		await this.findOwnedRecordOrThrow(itemId);

		if (this.currentTransferItemId === itemId && this.currentTransferAbortController !== null) {
			this.currentTransferAbortController.abort();
		}

		await this.localBufferPort.deleteItem(itemId);
		await this.outboxStore.deleteById(itemId);
		this.removeCacheEntry(itemId);
	}

	start(options: { readonly ownerUserId: number; readonly retentionMs: number | null }): void {
		if (this.isStarted) {
			this.stop();
		}

		this.isStarted = true;
		this.ownerUserId = options.ownerUserId;
		this.retentionMs = options.retentionMs;
		this.unsubscribeConnectivity = this.connectivityPort.subscribe(() => {
			this.scheduleQueueProcessing();
		});

		/**
		 * A hydration failure (no IndexedDB on this device, a denied quota, a corrupted database)
		 * must never resolve into a "started" runner sitting on an empty cache: `snapshot()` would
		 * then report an honest-looking empty queue while bytes the device is actually still holding
		 * go unrepresented — the exact silent lie `snapshot()`'s contract exists to rule out. Falling
		 * back to `stop()` instead restores the pre-start reading (the frozen empty array that
		 * promises nothing about the queue) rather than one that wrongly asserts it is empty. The
		 * ownership recheck mirrors the guard inside `hydrate()` itself: by the time this rejection is
		 * observed, a concurrent `stop()` followed by a fresh `start()` for a different owner may
		 * already be hydrating, and this catch must not tear that newer session down.
		 *
		 * `stop()` itself resets `hydrationStatus` to `'pending'` and notifies once; `'failed'` is
		 * written and notified again right after, inside this same ownership check, so it overrides
		 * that reset rather than racing a newer session's own write.
		 */
		void this.hydrate(options.ownerUserId).catch(() => {
			if (this.isStarted && this.ownerUserId === options.ownerUserId) {
				this.stop();
				this.hydrationStatus = 'failed';
				this.notifyListeners();
			}
		});
	}

	stop(): void {
		this.isStarted = false;
		this.ownerUserId = null;

		if (this.unsubscribeConnectivity !== null) {
			this.unsubscribeConnectivity();
			this.unsubscribeConnectivity = null;
		}
		if (this.scheduledTimerHandle !== null) {
			this.timer.cancel(this.scheduledTimerHandle);
			this.scheduledTimerHandle = null;
		}
		// Aborting here only reaches a transfer running in this very tab: an AbortController never
		// crosses tabs, so a concurrent tab's transfer keeps running until its own handler settles.
		if (this.currentTransferAbortController !== null) {
			this.currentTransferAbortController.abort();
			this.currentTransferAbortController = null;
			this.currentTransferItemId = null;
		}

		this.itemsCache.clear();
		this.cachedSnapshotArray = FROZEN_EMPTY_SNAPSHOT;
		this.hydrationStatus = 'pending';
		this.notifyListeners();
	}

	setRetention(retentionMs: number | null): void {
		this.retentionMs = retentionMs;
		if (!this.isStarted || this.ownerUserId === null) {
			return;
		}
		void this.catchUpRetentionAcrossAllOwners(this.ownerUserId);
	}

	/**
	 * Applies the exact same backfill-then-expire pass as hydration, over every owner this device
	 * holds — never only the current one, since expiry itself is never owner-scoped. Without this,
	 * the very first start-up following a deployment (where the persisted retention is still unknown,
	 * so the engine starts at `null` and hydration cannot backfill anything) would leave the entire
	 * inherited population immortal for the whole session — exactly the population this closes.
	 * Unlike `stop()`/`start()`, this never clears the cache or aborts an in-flight transfer: only
	 * items that actually change are refreshed in place.
	 */
	private async catchUpRetentionAcrossAllOwners(startedOwnerUserId: number): Promise<void> {
		const records = await this.outboxStore.listAll();
		const now = this.clock.now();

		for (const record of records) {
			if (!this.isStarted || this.ownerUserId !== startedOwnerUserId) {
				return;
			}
			const backfilledRecord = await this.backfillRetentionIfNeeded(record, now);
			const finalRecord = await this.expireIfEligible(backfilledRecord, now);
			if (finalRecord !== record) {
				await this.refreshCacheEntry(finalRecord);
			}
		}

		if (!this.isStarted || this.ownerUserId !== startedOwnerUserId) {
			return;
		}
		this.scheduleQueueProcessing();
	}

	private assertStarted(actionDescription: string): void {
		if (!this.isStarted) {
			throw new Error(`${actionDescription} nécessite que le moteur de synchronisation ait démarré.`);
		}
	}

	/**
	 * Loads a record and rejects one owned by a different user exactly as it would an unknown id:
	 * ownership is the only property that keeps a foreign item out of every snapshot, so a write path
	 * that told the two cases apart would leak whether a foreign id exists.
	 */
	private async findOwnedRecordOrThrow(itemId: string): Promise<OutboxRecord> {
		const record = await this.outboxStore.findById(itemId);
		if (record === null || record.ownerUserId !== this.ownerUserId) {
			throw new Error(ITEM_NOT_FOUND_MESSAGE);
		}

		return record;
	}

	/**
	 * Reads every record once, recovers anything an interrupted session left mid-flight, recalls
	 * `deleteItem` on anything already `'expired'`, backfills retention and evaluates expiry across
	 * every owner, then rebuilds the cache and notifies — in that order.
	 * `purgeExpiredOlderThan` runs last, deliberately after expiry evaluation: running it as the very
	 * first instruction (as `purgeConfirmedOlderThan24Hours` above does) would let a record erased
	 * between its `'expired'` write and the matching `deleteItem` — app killed in between — survive
	 * unreferenced forever, once 31 days have let the purge remove the only record that could still
	 * recall that deletion. Guarded repeatedly against `stop()` or a second `start()` racing this same
	 * async pass, since nothing here can be made atomic with them.
	 */
	private async hydrate(startedOwnerUserId: number): Promise<void> {
		await this.outboxStore.purgeConfirmedOlderThan24Hours();
		if (!this.isStarted || this.ownerUserId !== startedOwnerUserId) {
			return;
		}

		const records = await this.outboxStore.listAll();
		const now = this.clock.now();
		const reconciledRecords: OutboxRecord[] = [];

		for (const record of records) {
			reconciledRecords.push(await this.reconcileRecordOnHydration(record, now));
		}

		if (!this.isStarted || this.ownerUserId !== startedOwnerUserId) {
			return;
		}

		const finalRecords: OutboxRecord[] = [];
		for (const record of reconciledRecords) {
			const backfilledRecord = await this.backfillRetentionIfNeeded(record, now);
			finalRecords.push(await this.expireIfEligible(backfilledRecord, now));
		}

		if (!this.isStarted || this.ownerUserId !== startedOwnerUserId) {
			return;
		}

		this.itemsCache.clear();
		for (const record of finalRecords) {
			if (record.ownerUserId !== startedOwnerUserId) {
				continue;
			}
			const item = await this.composeItem(record);
			this.itemsCache.set(item.id, item);
		}

		this.hydrationStatus = 'ready';
		this.recomputeSnapshotArray();
		this.notifyListeners();

		await this.outboxStore.purgeExpiredOlderThan(EXPIRED_TRACE_RETENTION_MS);

		if (!this.isStarted || this.ownerUserId !== startedOwnerUserId) {
			return;
		}
		this.scheduleQueueProcessing();
	}

	/**
	 * `'transferring'`/`'verifying'` found at hydration time can only be a session interrupted
	 * mid-flight (killed tab, crash): this class never knows whether the server actually received
	 * that attempt, so the safe reading is "not confirmed", and the item goes back to `'pending'` for
	 * a fresh attempt. A `'draft'` is never promoted here: it leaves that state only through
	 * `markReady`, `discard` or retention expiry, since a quiet buffer cannot tell a crash from a
	 * pause. An already-`'expired'` record recalls `deleteItem` unconditionally: the one call this
	 * recovers from is an application killed between the `'expired'` write and its matching deletion,
	 * and `deleteItem` is idempotent, so a record whose bytes are already gone costs nothing extra.
	 */
	private async reconcileRecordOnHydration(record: OutboxRecord, now: number): Promise<OutboxRecord> {
		if (record.state === 'expired') {
			await this.localBufferPort.deleteItem(record.id);
			return record;
		}

		if (record.state === 'transferring' || record.state === 'verifying') {
			const recoveredRecord: OutboxRecord = {
				...record,
				state: 'pending',
				nextAttemptAt: null,
				progress: null,
				updatedAt: now,
			};
			await this.outboxStore.save(recoveredRecord);
			return recoveredRecord;
		}

		return record;
	}

	private async composeItem(record: OutboxRecord): Promise<OutboxItem> {
		const segments = await this.localBufferPort.listSegments(record.id);
		const segmentCount = segments.length;
		const byteSize = segments.reduce((total, segment) => total + segment.byteSize, 0);
		const expiresAt = record.retentionMs === null ? null : record.createdAt + record.retentionMs;

		return Object.freeze({ ...record, segmentCount, byteSize, expiresAt });
	}

	/**
	 * Backfills `retentionMs` from this engine's current retention when a record was created before
	 * any retention was known. The anchor stays `createdAt` — this is never a re-anchoring on the
	 * catch-up date, which would hand the oldest item on the device weeks of extra life — but the
	 * deadline is never allowed to land less than 72h from now (a deadline already in the past
	 * included): without this floor, an item created twenty days ago and backfilled today at fifteen
	 * days would compute an already-passed deadline and be destroyed in the very same pass, with the
	 * warning never having had a chance to show.
	 */
	private async backfillRetentionIfNeeded(record: OutboxRecord, now: number): Promise<OutboxRecord> {
		if (record.retentionMs !== null || this.retentionMs === null) {
			return record;
		}

		const candidateExpiresAt = record.createdAt + this.retentionMs;
		const effectiveRetentionMs =
			candidateExpiresAt - now < RETENTION_BACKFILL_GRACE_PERIOD_MS
				? now + RETENTION_BACKFILL_GRACE_PERIOD_MS - record.createdAt
				: this.retentionMs;

		const backfilledRecord: OutboxRecord = {
			...record,
			retentionMs: effectiveRetentionMs,
			updatedAt: now,
		};
		await this.outboxStore.save(backfilledRecord);

		return backfilledRecord;
	}

	/**
	 * Expires `'draft'`/`'pending'`/`'failed'` records whose retention has elapsed —
	 * `'transferring'`/`'verifying'` are never candidates (hydration already returns them to
	 * `'pending'` first) and `'confirmed'` bytes are already gone. `context` and `durationMs` are
	 * deliberately left untouched: they say which case and which duration were lost, and are never
	 * more than technical identifiers. The transition is written before the bytes are deleted — the
	 * reverse order would leave, on a crash in between, a `'pending'` record with no segment at all,
	 * which a transfer handler would read as a genuinely empty recording.
	 */
	private async expireIfEligible(record: OutboxRecord, now: number): Promise<OutboxRecord> {
		if (!isExpirableState(record.state)) {
			return record;
		}
		if (record.retentionMs === null || now - record.createdAt < record.retentionMs) {
			return record;
		}

		const expiredRecord: OutboxRecord = {
			...record,
			state: 'expired',
			expiredAt: now,
			progress: null,
			nextAttemptAt: null,
			lastErrorMessage: this.buildExpiryMessage(record.retentionMs),
			updatedAt: now,
		};
		await this.outboxStore.save(expiredRecord);
		await this.localBufferPort.deleteItem(record.id);

		return expiredRecord;
	}

	/** Built from `retentionMs`, never hard-coded: a policy change must never make this sentence lie. */
	private buildExpiryMessage(retentionMs: number): string {
		const retentionDays = Math.round(retentionMs / MILLISECONDS_PER_DAY);

		return `Cet enregistrement n'a jamais pu être transféré. Après ${retentionDays} jours sur l'appareil, il en a été définitivement effacé.`;
	}

	private upsertCacheEntry(item: OutboxItem): void {
		if (this.ownerUserId === null || item.ownerUserId !== this.ownerUserId) {
			return;
		}
		this.itemsCache.set(item.id, item);
		this.recomputeSnapshotArray();
		this.notifyListeners();
	}

	private removeCacheEntry(itemId: string): void {
		const existed = this.itemsCache.delete(itemId);
		if (existed) {
			this.recomputeSnapshotArray();
			this.notifyListeners();
		}
	}

	private recomputeSnapshotArray(): void {
		this.cachedSnapshotArray = Object.freeze(Array.from(this.itemsCache.values()));
	}

	private notifyListeners(): void {
		const snapshotToDeliver = this.snapshot();
		for (const listener of this.listeners) {
			listener(snapshotToDeliver);
		}
	}

	/**
	 * Re-reads one record from storage and applies `updater` to it, persisting and re-caching only if
	 * something actually changed. A `null` return means the record vanished under us — a concurrent
	 * `discard()` — in which case the stale cache entry, if any, is dropped.
	 */
	private async transitionRecord(
		itemId: string,
		updater: (record: OutboxRecord) => OutboxRecord,
	): Promise<OutboxRecord | null> {
		const record = await this.outboxStore.findById(itemId);
		if (record === null) {
			this.removeCacheEntry(itemId);
			return null;
		}

		const updatedRecord = updater(record);
		if (updatedRecord === record) {
			return record;
		}

		await this.outboxStore.save(updatedRecord);
		const item = await this.composeItem(updatedRecord);
		this.upsertCacheEntry(item);

		return updatedRecord;
	}

	private scheduleQueueProcessing(): void {
		if (!this.isStarted) {
			return;
		}
		void this.runQueueProcessingCycle();
	}

	private async runQueueProcessingCycle(): Promise<void> {
		if (this.isProcessingQueue) {
			return;
		}
		this.isProcessingQueue = true;

		try {
			if (this.lockManager !== undefined) {
				await this.lockManager.request(OUTBOX_TRANSFER_LOCK_NAME, async () => {
					await this.drainEligibleItems();
				});
			} else {
				await this.drainEligibleItems();
			}
		} finally {
			this.isProcessingQueue = false;
		}
	}

	private async drainEligibleItems(): Promise<void> {
		await this.expireEligibleCachedItems();

		while (this.isStarted) {
			const nextItem = this.selectNextEligibleItem();
			if (nextItem === undefined) {
				break;
			}
			await this.attemptTransfer(nextItem);
		}
		this.scheduleNextWakeUp();
	}

	/**
	 * Evaluates expiry over the current owner's cached items only — this is the trigger that covers a
	 * session left open for days, and by consequence every connectivity change; hydration is the one
	 * that sees every owner on the device.
	 */
	private async expireEligibleCachedItems(): Promise<void> {
		const now = this.clock.now();
		const candidateItems = Array.from(this.itemsCache.values()).filter(
			(item) =>
				isExpirableState(item.state) &&
				item.retentionMs !== null &&
				now - item.createdAt >= item.retentionMs,
		);

		for (const candidateItem of candidateItems) {
			if (!this.isStarted) {
				return;
			}
			const record = await this.outboxStore.findById(candidateItem.id);
			if (record === null) {
				this.removeCacheEntry(candidateItem.id);
				continue;
			}
			const expiredRecord = await this.expireIfEligible(record, now);
			if (expiredRecord !== record) {
				await this.refreshCacheEntry(expiredRecord);
			}
		}
	}

	private selectNextEligibleItem(): OutboxItem | undefined {
		if (!this.connectivityPort.current().isOnline) {
			return undefined;
		}

		const now = this.clock.now();
		let earliestEligibleItem: OutboxItem | undefined;

		for (const candidateItem of this.itemsCache.values()) {
			if (candidateItem.state !== 'pending') {
				continue;
			}
			if (candidateItem.nextAttemptAt !== null && candidateItem.nextAttemptAt > now) {
				continue;
			}
			if (earliestEligibleItem === undefined) {
				earliestEligibleItem = candidateItem;
				continue;
			}
			const candidateOrder = candidateItem.pendingSince ?? candidateItem.createdAt;
			const earliestOrder = earliestEligibleItem.pendingSince ?? earliestEligibleItem.createdAt;
			if (candidateOrder < earliestOrder) {
				earliestEligibleItem = candidateItem;
			}
		}

		return earliestEligibleItem;
	}

	/**
	 * Wakes up on the earlier of two clocks: the next transfer retry among `'pending'` items, and the
	 * next expiry among `'draft'`/`'pending'`/`'failed'` items. A version that only ever looked at
	 * `nextAttemptAt` would never wake up for a queue made only of `'failed'` or untouched `'draft'`
	 * items — exactly the states most likely to reach their retention deadline unattended.
	 */
	private scheduleNextWakeUp(): void {
		if (this.scheduledTimerHandle !== null) {
			this.timer.cancel(this.scheduledTimerHandle);
			this.scheduledTimerHandle = null;
		}
		if (!this.isStarted) {
			return;
		}

		let earliestNextAttemptAt: number | null = null;
		let earliestExpiresAt: number | null = null;
		for (const item of this.itemsCache.values()) {
			if (
				item.state === 'pending' &&
				item.nextAttemptAt !== null &&
				(earliestNextAttemptAt === null || item.nextAttemptAt < earliestNextAttemptAt)
			) {
				earliestNextAttemptAt = item.nextAttemptAt;
			}
			if (
				isExpirableState(item.state) &&
				item.expiresAt !== null &&
				(earliestExpiresAt === null || item.expiresAt < earliestExpiresAt)
			) {
				earliestExpiresAt = item.expiresAt;
			}
		}

		if (earliestNextAttemptAt === null && earliestExpiresAt === null) {
			return;
		}

		const candidates = [earliestNextAttemptAt, earliestExpiresAt].filter(
			(candidate): candidate is number => candidate !== null,
		);
		const earliestWakeUpAt = Math.min(...candidates);

		const delayMs = Math.max(0, earliestWakeUpAt - this.clock.now());
		this.scheduledTimerHandle = this.timer.schedule(() => {
			this.scheduledTimerHandle = null;
			this.scheduleQueueProcessing();
		}, delayMs);
	}

	/**
	 * Re-reads the record fresh from storage before claiming it, rather than trusting the cached
	 * `item` passed in: the cache can be stale relative to another tab holding the transfer lock. If
	 * the fresh read disagrees with the cache — already claimed, already settled, discarded, or its
	 * backoff has not elapsed after all — this resyncs the cache instead of claiming, so the same
	 * stale entry is never re-selected in a tight loop.
	 */
	private async claimPendingItem(itemId: string): Promise<OutboxRecord | null> {
		const record = await this.outboxStore.findById(itemId);
		if (record === null) {
			this.removeCacheEntry(itemId);
			return null;
		}
		if (record.state !== 'pending') {
			await this.refreshCacheEntry(record);
			return null;
		}
		const now = this.clock.now();
		if (record.nextAttemptAt !== null && record.nextAttemptAt > now) {
			await this.refreshCacheEntry(record);
			return null;
		}

		const claimedRecord: OutboxRecord = {
			...record,
			state: 'transferring',
			progress: null,
			updatedAt: now,
		};
		await this.outboxStore.save(claimedRecord);
		const item = await this.composeItem(claimedRecord);
		this.upsertCacheEntry(item);

		return claimedRecord;
	}

	private async refreshCacheEntry(record: OutboxRecord): Promise<void> {
		const item = await this.composeItem(record);
		this.upsertCacheEntry(item);
	}

	private async attemptTransfer(item: OutboxItem): Promise<void> {
		const claimedRecord = await this.claimPendingItem(item.id);
		if (claimedRecord === null) {
			return;
		}

		const handler = this.handlersByKind.get(claimedRecord.kind);
		if (handler === undefined) {
			await this.failWithoutAttempt(claimedRecord.id, NO_HANDLER_MESSAGE);
			return;
		}

		const segmentsResult = await this.assembleSegmentsWithDigests(
			claimedRecord.id,
			claimedRecord.mimeType,
		);
		if (segmentsResult === null) {
			await this.scheduleRetryAfterTransientFailure(claimedRecord.id, TRANSIENT_READ_FAILURE_MESSAGE);
			return;
		}
		const { transferSegments, expectedDigestsBySegment } = segmentsResult;

		const abortController = new AbortController();
		this.currentTransferAbortController = abortController;
		this.currentTransferItemId = claimedRecord.id;

		let outcome: TransferOutcome;
		try {
			const itemForHandler = await this.composeItem(claimedRecord);
			outcome = await handler({
				item: itemForHandler,
				segments: transferSegments,
				report: (phase: TransferPhase) => {
					this.applyProgressReport(claimedRecord.id, phase);
				},
				signal: abortController.signal,
			});
		} catch {
			this.currentTransferAbortController = null;
			this.currentTransferItemId = null;
			if ((await this.outboxStore.findById(claimedRecord.id)) === null) {
				this.removeCacheEntry(claimedRecord.id);
				return;
			}
			await this.scheduleRetryAfterTransientFailure(
				claimedRecord.id,
				UNEXPECTED_HANDLER_FAILURE_MESSAGE,
			);
			return;
		}

		this.currentTransferAbortController = null;
		this.currentTransferItemId = null;

		if ((await this.outboxStore.findById(claimedRecord.id)) === null) {
			this.removeCacheEntry(claimedRecord.id);
			return;
		}

		if (outcome.status === 'retry') {
			await this.scheduleRetryAfterTransientFailure(claimedRecord.id, outcome.reason);
			return;
		}
		if (outcome.status === 'failed') {
			await this.transitionToPermanentFailure(claimedRecord.id, outcome.reason);
			return;
		}
		if (outcome.status === 'orphaned') {
			await this.transitionToOrphaned(claimedRecord.id, outcome.reason);
			return;
		}

		await this.verifyAndFinalize(claimedRecord.id, expectedDigestsBySegment, outcome.verifiedDigests);
	}

	private applyProgressReport(itemId: string, phase: TransferPhase): void {
		void this.transitionRecord(itemId, (record) => {
			if (record.state !== 'transferring') {
				return record;
			}
			return { ...record, progress: phase.progress, updatedAt: this.clock.now() };
		});
	}

	/** Assembles and digests every segment before anything is handed to a handler — step one of the integrity barrier. */
	private async assembleSegmentsWithDigests(
		itemId: string,
		mimeType: string,
	): Promise<{
		readonly transferSegments: readonly TransferSegment[];
		readonly expectedDigestsBySegment: ReadonlyMap<number, string>;
	} | null> {
		try {
			const segmentRefs = await this.localBufferPort.listSegments(itemId);
			const transferSegments: TransferSegment[] = [];
			const expectedDigestsBySegment = new Map<number, string>();

			for (const segmentRef of segmentRefs) {
				const blob = await this.localBufferPort.assembleSegment(itemId, segmentRef.segment, mimeType);
				const digest = await this.computeDigest(blob);
				transferSegments.push({ segment: segmentRef.segment, blob });
				expectedDigestsBySegment.set(segmentRef.segment, digest);
			}

			return { transferSegments, expectedDigestsBySegment };
		} catch {
			return null;
		}
	}

	/** No genuine attempt happened — the handler was never called — so `attempts` is left untouched. */
	private async failWithoutAttempt(itemId: string, message: string): Promise<void> {
		const now = this.clock.now();
		await this.transitionRecord(itemId, (record) => ({
			...record,
			state: 'failed',
			lastErrorMessage: message,
			nextAttemptAt: null,
			progress: null,
			updatedAt: now,
		}));
	}

	private async scheduleRetryAfterTransientFailure(itemId: string, reason: string): Promise<void> {
		const now = this.clock.now();
		await this.transitionRecord(itemId, (record) => {
			const attemptCount = record.attempts + 1;
			return {
				...record,
				state: 'pending',
				attempts: attemptCount,
				nextAttemptAt: now + this.computeBackoffDelay(attemptCount),
				lastErrorMessage: reason,
				progress: null,
				updatedAt: now,
			};
		});
	}

	private async transitionToOrphaned(itemId: string, reason: string): Promise<void> {
		const now = this.clock.now();
		await this.transitionRecord(itemId, (record) => ({
			...record,
			state: 'orphaned',
			lastErrorMessage: reason,
			nextAttemptAt: null,
			progress: null,
			updatedAt: now,
		}));
	}

	private async transitionToPermanentFailure(itemId: string, reason: string): Promise<void> {
		const now = this.clock.now();
		await this.transitionRecord(itemId, (record) => ({
			...record,
			state: 'failed',
			attempts: record.attempts + 1,
			lastErrorMessage: reason,
			nextAttemptAt: null,
			progress: null,
			updatedAt: now,
		}));
	}

	/**
	 * Increments `integrityFailureCount` first, then dispatches on its new value. The first failure
	 * (`0 → 1`) is transient: the item goes back to `'pending'` with backoff, so the next automatic
	 * attempt resends every segment in full and the server's own upsert overwrites the diverging
	 * position — this is what makes A-09 bis's healing a property of the design rather than something
	 * suspended on a manual `retryNow()` nobody triggers. A second failure is no longer a botched
	 * transfer but a local corruption a further resend will not heal, so it becomes `'failed'` for
	 * good. The count is never reset to zero: it only disables an optimisation on the handler's side,
	 * and resetting it after one successful transfer would reopen the same skip/resend oscillation
	 * against a server that stays durably divergent.
	 */
	private async transitionToIntegrityFailure(itemId: string, reason: string): Promise<void> {
		const now = this.clock.now();
		await this.transitionRecord(itemId, (record) => {
			const integrityFailureCount = record.integrityFailureCount + 1;
			const attempts = record.attempts + 1;
			if (integrityFailureCount === 1) {
				return {
					...record,
					state: 'pending',
					attempts,
					integrityFailureCount,
					nextAttemptAt: now + this.computeBackoffDelay(attempts),
					lastErrorMessage: reason,
					progress: null,
					updatedAt: now,
				};
			}
			return {
				...record,
				state: 'failed',
				attempts,
				integrityFailureCount,
				lastErrorMessage: reason,
				nextAttemptAt: null,
				progress: null,
				updatedAt: now,
			};
		});
	}

	/**
	 * Enters `'verifying'` itself before comparing anything — the step that exists even though the
	 * handler already reported success — and only an exact, paired match across every segment reaches
	 * the deletion below. `context`, `durationMs` and `pendingSince` are cleared on confirmation:
	 * nothing that could identify which case or interview this was survives past this point, only the
	 * display-safe `{ id, kind, state, confirmedAt }`.
	 */
	private async verifyAndFinalize(
		itemId: string,
		expectedDigestsBySegment: ReadonlyMap<number, string>,
		verifiedDigests: readonly VerifiedSegmentDigest[],
	): Promise<void> {
		const verifyingRecord = await this.transitionRecord(itemId, (record) => ({
			...record,
			state: 'verifying',
			progress: null,
			updatedAt: this.clock.now(),
		}));
		if (verifyingRecord === null) {
			return;
		}

		const integrityResult = this.evaluateIntegrity(expectedDigestsBySegment, verifiedDigests);
		if (!integrityResult.matched) {
			await this.transitionToIntegrityFailure(
				itemId,
				INTEGRITY_FAILURE_MESSAGES[integrityResult.reason],
			);
			return;
		}

		await this.localBufferPort.deleteItem(itemId);
		const now = this.clock.now();
		await this.transitionRecord(itemId, (record) => ({
			...record,
			state: 'confirmed',
			confirmedAt: now,
			updatedAt: now,
			context: {},
			durationMs: null,
			pendingSince: null,
			nextAttemptAt: null,
			progress: null,
			lastErrorMessage: null,
		}));
		await this.outboxStore.purgeConfirmedOlderThan24Hours();
	}

	/**
	 * Compares by segment number and by count, never as sets of digest values: two empty fragments
	 * share a digest, so only pairing by segment tells them apart. Cardinality is checked before
	 * content so "more digests than segments" and "fewer digests than segments" are never confused
	 * with a content mismatch.
	 */
	private evaluateIntegrity(
		expectedDigestsBySegment: ReadonlyMap<number, string>,
		verifiedDigests: readonly VerifiedSegmentDigest[],
	): { readonly matched: true } | { readonly matched: false; readonly reason: IntegrityFailureReason } {
		if (verifiedDigests.length > expectedDigestsBySegment.size) {
			return { matched: false, reason: 'extra' };
		}
		if (verifiedDigests.length < expectedDigestsBySegment.size) {
			return { matched: false, reason: 'missing' };
		}

		const receivedSegmentNumbers = new Set<number>();
		for (const verifiedDigest of verifiedDigests) {
			receivedSegmentNumbers.add(verifiedDigest.segment);
		}
		if (receivedSegmentNumbers.size !== verifiedDigests.length) {
			return { matched: false, reason: 'extra' };
		}

		for (const verifiedDigest of verifiedDigests) {
			if (!expectedDigestsBySegment.has(verifiedDigest.segment)) {
				return { matched: false, reason: 'mismatched-segment' };
			}
		}

		for (const [segmentNumber, expectedDigest] of expectedDigestsBySegment) {
			const matchingVerifiedDigest = verifiedDigests.find(
				(verifiedDigest) => verifiedDigest.segment === segmentNumber,
			);
			if (matchingVerifiedDigest === undefined) {
				return { matched: false, reason: 'missing' };
			}
			if (matchingVerifiedDigest.digest !== expectedDigest) {
				return { matched: false, reason: 'divergent' };
			}
		}

		return { matched: true };
	}
}

export default OutboxRunner;
